import { WorkbookPage, SectionTitle, Paragraph, Callout, StepList } from '@/components/WorkbookPage';
import { BookOpen, Pencil, CheckSquare, RotateCcw, Users } from 'lucide-react';

export const HowToUsePage = () => {
  return (
    <WorkbookPage pageId="how-to-use" prevPage="copyright" nextPage="toc">
      <header className="mb-10">
        <h1 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-4">
          How to Use This Workbook
        </h1>
        <p className="text-lg text-muted-foreground">
          Your guide to getting the most out of your financial journey
        </p>
      </header>
      
      <Paragraph>
        Welcome! This workbook is designed to meet you exactly where you are. Whether you're 
        just starting to manage money, rebuilding after a setback, or looking to grow what 
        you've built—there's something here for you.
      </Paragraph>
      
      <Paragraph>
        <strong>You don't need to be a math person.</strong> You don't need a certain income level. 
        You just need to be willing to try. That's it.
      </Paragraph>
      
      {/* Key features */}
      <div className="grid sm:grid-cols-2 gap-4 my-8">
        <div className="bg-secondary/50 rounded-lg p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <BookOpen className="h-5 w-5 text-primary" />
            </div>
            <h3 className="font-semibold">Read First</h3>
          </div>
          <p className="text-sm text-muted-foreground">
            Each rule starts with a clear explanation. Read it through before writing anything.
          </p>
        </div>
        
        <div className="bg-secondary/50 rounded-lg p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Pencil className="h-5 w-5 text-primary" />
            </div>
            <h3 className="font-semibold">Write It Down</h3>
          </div>
          <p className="text-sm text-muted-foreground">
            Use the worksheets. Writing helps you process and remember. Don't just think it—ink it.
          </p>
        </div>
        
        <div className="bg-secondary/50 rounded-lg p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <CheckSquare className="h-5 w-5 text-primary" />
            </div>
            <h3 className="font-semibold">Check Boxes</h3>
          </div>
          <p className="text-sm text-muted-foreground">
            Use checkboxes to track progress. Small wins matter. Celebrate each one.
          </p>
        </div>
        
        <div className="bg-secondary/50 rounded-lg p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <RotateCcw className="h-5 w-5 text-primary" />
            </div>
            <h3 className="font-semibold">Come Back</h3>
          </div>
          <p className="text-sm text-muted-foreground">
            Life changes. Revisit sections as needed. This workbook grows with you.
          </p>
        </div>
      </div>
      
      <SectionTitle>The Structure</SectionTitle>
      
      <Paragraph>
        Each "Rule" follows the same format so you always know what to expect:
      </Paragraph>
      
      <div className="space-y-3 mb-8">
        {[
          { num: 1, title: 'Rule Overview', desc: 'What this rule means in plain English' },
          { num: 2, title: 'Why It Matters', desc: 'The real-world reason this helps you' },
          { num: 3, title: 'Examples', desc: 'Real-life scenarios you can relate to' },
          { num: 4, title: 'Step-by-Step', desc: 'Exactly what to do, in order' },
          { num: 5, title: 'Worksheet', desc: 'Space to apply it to YOUR life' },
          { num: 6, title: 'Quick Recap', desc: 'Summary + affirmation to remember' },
          { num: 7, title: 'Optional Reflection', desc: 'Space for deeper thought (always optional)' },
        ].map((item) => (
          <div key={item.num} className="flex items-start gap-4">
            <div className="w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold shrink-0">
              {item.num}
            </div>
            <div>
              <span className="font-semibold">{item.title}</span>
              <span className="text-muted-foreground"> — {item.desc}</span>
            </div>
          </div>
        ))}
      </div>
      
      <Callout type="tip" title="Go at Your Own Pace">
        There's no rush. Some people complete one rule per week. Others do more. 
        What matters is that you're moving forward, even if it's slow.
      </Callout>
      
      <SectionTitle>For Group Use</SectionTitle>
      
      <div className="flex items-start gap-4 bg-muted/50 rounded-lg p-5 mb-8">
        <Users className="h-6 w-6 text-primary shrink-0 mt-1" />
        <div>
          <p className="text-sm leading-relaxed">
            This workbook works great in groups! Whether it's a class, support group, 
            church group, or friends meeting up—you can go through it together. 
            See the back of this workbook for facilitator guidelines.
          </p>
        </div>
      </div>
      
      <SectionTitle>Your Progress is Saved</SectionTitle>
      
      <Paragraph>
        Everything you write in this digital workbook is automatically saved to your device. 
        You can export your data anytime, and come back right where you left off.
      </Paragraph>
      
      <div className="affirmation-box my-8">
        <p className="text-lg font-serif">
          "You don't have to be great to start, but you have to start to be great."
        </p>
      </div>
      
      <Paragraph className="text-center text-muted-foreground">
        Ready? Let's begin. 💚
      </Paragraph>
    </WorkbookPage>
  );
};
